/**
 * Context Relevance Evaluation
 *
 * Utilities for evaluating conversation history relevance based on:
 * - Time gaps between messages
 * - Topic continuity signals
 * - Clarification state tracking
 *
 * This module ensures that agentic workflows don't incorrectly assume
 * that new messages are related to previous conversation context.
 */

import type { ChatMessage } from './types';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Time gap thresholds for context relevance evaluation.
 * Messages older than these thresholds may have reduced relevance.
 */
export interface TimeGapThresholds {
  /** Gap considered "recent" - full relevance (default: 5 minutes) */
  recentMs: number;
  /** Gap considered "moderate" - partial relevance (default: 30 minutes) */
  moderateMs: number;
  /** Gap considered "stale" - low relevance (default: 2 hours) */
  staleMs: number;
  /** Gap considered "expired" - ignore unless explicitly referenced (default: 24 hours) */
  expiredMs: number;
}

export const DEFAULT_TIME_GAP_THRESHOLDS: TimeGapThresholds = {
  recentMs: 5 * 60 * 1000,        // 5 minutes
  moderateMs: 30 * 60 * 1000,     // 30 minutes
  staleMs: 2 * 60 * 60 * 1000,    // 2 hours
  expiredMs: 24 * 60 * 60 * 1000, // 24 hours
};

// ============================================================================
// Time Gap Calculation
// ============================================================================

/**
 * Calculate time gap between current time and the most recent message.
 *
 * @param history - Array of chat messages
 * @returns Time gap in milliseconds, or null if no history
 */
export function calculateTimeGap(history: ChatMessage[]): number | null {
  if (!history || history.length === 0) return null;

  const lastMessage = history[history.length - 1];
  if (!lastMessage.timestamp) return null;

  const lastTimestamp = lastMessage.timestamp instanceof Date
    ? lastMessage.timestamp.getTime()
    : new Date(lastMessage.timestamp).getTime();

  if (isNaN(lastTimestamp)) return null;

  return Date.now() - lastTimestamp;
}

/**
 * Format time gap for human-readable display.
 *
 * @param timeGapMs - Time gap in milliseconds
 * @returns Human-readable string (e.g., "5 minutes", "2 hours")
 */
export function formatTimeGapDisplay(timeGapMs: number | null): string | null {
  if (timeGapMs === null) return null;

  const minutes = Math.round(timeGapMs / 60000);
  const hours = Math.round(timeGapMs / 3600000);
  const days = Math.round(timeGapMs / 86400000);

  if (minutes < 1) {
    return 'just now';
  } else if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
}

// ============================================================================
// Time Relevance Evaluation
// ============================================================================

/**
 * Evaluate time-based relevance category.
 *
 * @param timeGapMs - Time gap in milliseconds
 * @param thresholds - Custom thresholds (optional)
 * @returns Category and score
 */
export function evaluateTimeRelevance(
  timeGapMs: number | null,
  thresholds: TimeGapThresholds = DEFAULT_TIME_GAP_THRESHOLDS
): { category: 'high' | 'medium' | 'low' | 'stale'; score: number } {
  if (timeGapMs === null) {
    // No history = treat as fresh start with high relevance for any new content
    return { category: 'high', score: 1.0 };
  }

  if (timeGapMs <= thresholds.recentMs) {
    return { category: 'high', score: 1.0 };
  } else if (timeGapMs <= thresholds.moderateMs) {
    return { category: 'medium', score: 0.7 };
  } else if (timeGapMs <= thresholds.staleMs) {
    return { category: 'low', score: 0.4 };
  } else {
    return { category: 'stale', score: 0.1 };
  }
}

// ============================================================================
// Topic Signal Detection
// ============================================================================

/**
 * Detect topic change signals in the current message.
 * Looks for patterns that indicate a new topic vs. continuation.
 *
 * @param currentMessage - The current user message text
 * @returns Detection result with signals found
 */
export function detectTopicChangeSignals(currentMessage: string): {
  hasNewTopicSignals: boolean;
  hasContinuitySignals: boolean;
  newTopicSignals: string[];
  continuitySignals: string[];
} {
  const message = currentMessage.toLowerCase().trim();

  // Patterns indicating a NEW topic (history likely NOT relevant)
  const newTopicPatterns = [
    /\b(new question|different topic|something else|forget that|moving on|new subject|unrelated|start fresh|separate question)\b/i,
    /\b(hello|hi|hey)\s*(again|there)?\s*[,.]?\s*(i want|i need|can you|please|help)\b/i,
    /^(hello|hi|hey)[,.]?\s*$/i, // Standalone greetings
    /\b(by the way|btw|on a different note|changing subject)\b/i,
  ];

  // Patterns indicating CONTINUATION (history IS relevant)
  const continuityPatterns = [
    /\b(that|it|this|those|the same|continue|also|now|next|then|keep going)\b/i,
    /\b(as we discussed|like i mentioned|from before|the document|the file|my earlier)\b/i,
    /\b(translate it|format that|research that further|now do|based on that)\b/i,
    /\b(why\?|how\?|what about|can you also|and also|more about)\b/i,
    /\b(tell me more|expand on|go deeper|elaborate|explain further)\b/i,
    /\b(the report|the research|my document|that document)\b/i,
  ];

  const newTopicSignals: string[] = [];
  const continuitySignals: string[] = [];

  for (const pattern of newTopicPatterns) {
    const match = message.match(pattern);
    if (match) newTopicSignals.push(match[0]);
  }

  for (const pattern of continuityPatterns) {
    const match = message.match(pattern);
    if (match) continuitySignals.push(match[0]);
  }

  return {
    hasNewTopicSignals: newTopicSignals.length > 0,
    hasContinuitySignals: continuitySignals.length > 0,
    newTopicSignals,
    continuitySignals,
  };
}

// (D13-6b, 2026-08-25: the orchestrator-only half of this module —
// getMostRecentContext, generateContextHash, evaluateContextRelevance,
// formatTimeGapForPrompt, generateContextClarificationQuestion and their
// result types — was pruned when the file moved up out of the deleted
// orchestrator/ directory. Their only consumers died with the brain.)
